import React, { useState, useEffect, useRef } from 'react';
import { cartService } from '../../services/cartService';
import { categoriesApi, Category } from '../../services/api';
import logoImage from '../../assets/NNN-logo.png';
import './Header.css';

interface HeaderProps {
    onCartClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onCartClick }) => {
    const [cartCount, setCartCount] = useState(0);
    const [categories, setCategories] = useState<Category[]>([]);
    const [showCategories, setShowCategories] = useState(false);
    const [headerVisible, setHeaderVisible] = useState(true);
    const [headerScrolled, setHeaderScrolled] = useState(false);
    const lastScrollY = useRef(0);

    // Mobile only: hide header on scroll down, show on scroll up
    useEffect(() => {
        const isMobile = () => window.innerWidth <= 768;
        const handleScroll = () => {
            setHeaderScrolled(window.scrollY > 20);
            if (!isMobile()) return;
            const currentScrollY = window.scrollY;
            if (currentScrollY <= 10) {
                setHeaderVisible(true);
            } else if (currentScrollY > lastScrollY.current) {
                setHeaderVisible(false);
            } else {
                setHeaderVisible(true);
            }
            lastScrollY.current = currentScrollY;
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const updateCartCount = () => {
            setCartCount(cartService.getCartCount());
        };

        updateCartCount();
        window.addEventListener('cartUpdated', updateCartCount);

        // Fetch categories
        const fetchCategories = async () => {
            try {
                const response = await categoriesApi.getAll();
                setCategories(response.data.categories || []);
            } catch (err) {
                console.error('Error fetching categories:', err);
            }
        };

        fetchCategories();

        return () => {
            window.removeEventListener('cartUpdated', updateCartCount);
        };
    }, []);

    const scrollToCategory = (categoryId: string) => {
        const element = document.getElementById(`category-${categoryId}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        setShowCategories(false);
    };

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const headerOffset = 100; // Account for fixed header
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    return (
        <header className={`header ${headerScrolled ? 'header--scrolled' : ''} ${!headerVisible ? 'header--mobile-hidden' : ''}`}>
            <div className="container">
                <div className="header-content">
                    <div className="logo">
                        <a href="/" title="Namma Nesavu Nool — Home" aria-label="Namma Nesavu Nool home">
                            <img src={logoImage} alt="Namma Nesavu Nool — handloom textiles and school uniforms" className="logo-image" loading="eager" decoding="async" />
                        </a>
                    </div>
                    <nav className="nav" aria-label="Main navigation">
                        <div
                            className="nav-item categories-dropdown"
                            onMouseEnter={() => setShowCategories(true)}
                            onMouseLeave={() => setShowCategories(false)}
                        >
                        <a href="#categories" className="nav-link">Products</a>
                            {showCategories && categories.length > 0 && (
                                <div className="categories-menu">
                                    {categories.map((category) => (
                                        <a
                                            key={category.id}
                                            href={`#category-${category.id}`}
                                            className="category-link"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                scrollToCategory(category.id);
                                            }}
                                        >
                                            {category.name}
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                        <a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }}>Learn</a>
                        <a href="#our-work" onClick={(e) => { e.preventDefault(); scrollToSection('our-work'); }}>Our Work</a>
                        <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>Contact</a>
                    </nav>
                    <div className="header-actions">
                        <a
                            href="https://www.instagram.com/nammanesavunool/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="instagram-link"
                            title="Follow us on Instagram"
                        >
                            <span className="instagram-icon">📷</span>
                        </a>
                        <div className="phone-card">
                            <div className="phone-icon">📞</div>
                            <div className="phone-info">
                                <span className="phone-label">Call Us</span>
                                <span className="phone-number">+91 9655007980</span>
                            </div>
                        </div>
                        {onCartClick && (
                            <button className="cart-icon-btn" onClick={onCartClick} title="Shopping Cart">
                                <span className="cart-icon">🛒</span>
                                {cartCount > 0 && (
                                    <span className="cart-badge">{cartCount}</span>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;
